import { ApiError } from '@daater/api';
import { apiClient } from '../lib/api-client';
import type {
  AcceptInvitationPayload,
  ApiResponse,
  AccountResponse,
  CheckCompanyResponse,
  CountryItem,
  CreatePaymentOrderPayload,
  Dataset,
  InvitationUserData,
  LoginPayload,
  LoginResponseData,
  PayuConfirmationPayload,
  PaymentHistoryResponse,
  PaymentOrder,
  RegisterPayload,
  SavedReportItem,
  SavedReportListResponse,
  SignupDemoPayload,
  TableField,
  TableList,
  TableFiltersResponse,
  TableRecords,
  UpdateSettingsPayload,
} from './types';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const CURRENT_ACCOUNT_PATH = '/accounts/team/current';
const PAYMENT_ROUTE_PATH = '/payment';
const LOGIN_ROUTE_PATH = '/login';

const toPositiveNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return undefined;
};

const shouldBlockForPayment = (account?: AccountResponse | null) => {
  if (!account) return false;
  if (account.statusCode === 402) return true;
  return account.status === 'trial' && typeof account.daysLeft === 'number' && account.daysLeft < 0;
};

const redirectToPayment = (amount?: unknown) => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === PAYMENT_ROUTE_PATH) return;

  const params = new URLSearchParams(window.location.search);
  const normalizedAmount = toPositiveNumber(amount);

  if (normalizedAmount !== undefined) {
    params.set('amount', String(normalizedAmount));
  }

  const nextUrl = params.toString()
    ? `${PAYMENT_ROUTE_PATH}?${params.toString()}`
    : PAYMENT_ROUTE_PATH;

  window.history.replaceState({}, '', nextUrl);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === LOGIN_ROUTE_PATH) return;
  window.history.replaceState({}, '', LOGIN_ROUTE_PATH);
  window.dispatchEvent(new PopStateEvent('popstate'));
};

const buildPaymentRequiredError = (account?: AccountResponse) =>
  new ApiError('Payment Required', 402, {
    success: true,
    statusCode: 402,
    data: account,
  });

export const normalizeCurrentAccountResponse = (data: AccountResponse): AccountResponse => {
  const user = data.user;
  const team = data.team;
  const userRecord = isRecord(user) ? user : undefined;
  const teamRecord = isRecord(team) ? team : undefined;

  return {
    ...data,
    user: typeof user === 'string' ? user : (userRecord?._id ?? user),
    team: typeof team === 'string' ? team : (teamRecord?._id ?? team),
    userData: userRecord ?? data.userData,
    teamData: teamRecord ?? data.teamData,
  };
};

const fetchCurrentAccountResponse = async () => {
  return apiClient.get<ApiResponse<AccountResponse>>(CURRENT_ACCOUNT_PATH);
};

const fetchGuardedCurrentAccount = async () => {
  const response = await fetchCurrentAccountResponse();
  return normalizeCurrentAccountResponse(response.data);
};

const ensureActiveAccountAccess = async () => {
  try {
    const account = await fetchGuardedCurrentAccount();

    if (shouldBlockForPayment(account)) {
      redirectToPayment(account.amount);
      throw buildPaymentRequiredError(account);
    }

    return account;
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 401) {
        redirectToLogin();
      }

      if (error.status === 402) {
        const responseData = error.data as ApiResponse<AccountResponse> | undefined;
        const account = responseData?.data
          ? normalizeCurrentAccountResponse(responseData.data)
          : undefined;

        redirectToPayment(account?.amount);
        throw buildPaymentRequiredError(account);
      }
    }

    throw error;
  }
};

const guardedGet = async <T>(path: string) => {
  await ensureActiveAccountAccess();
  return apiClient.get<T>(path);
};

const guardedPost = async <T>(path: string, body?: unknown) => {
  await ensureActiveAccountAccess();
  return apiClient.post<T>(path, body);
};

const guardedPut = async <T>(path: string, body?: unknown) => {
  await ensureActiveAccountAccess();
  return apiClient.put<T>(path, body);
};

const guardedDelete = async <T>(path: string) => {
  await ensureActiveAccountAccess();
  return apiClient.del<T>(path);
};

export const loginRequest = async (payload: LoginPayload) => {
  const response = await apiClient.post<ApiResponse<LoginResponseData>>('/auth/login', payload);

  if (response.statusCode === 401) {
    const message = response.error?.detail ?? 'Invalid credentials';
    throw new Error(message);
  }

  return response.data;
};

export const updateTerms = async () => {
  return apiClient.put<ApiResponse<Record<string, unknown>>>('/accounts/update/', {
    terms: true,
  });
};

export const fetchCurrentAccount = async () => {
  return fetchGuardedCurrentAccount();
};

export const fetchCurrentTeam = async (userId: string) => {
  const response =
    await guardedGet<
      ApiResponse<{ items: Array<{ user: string; team: Record<string, unknown> }> }>
    >('/accounts/team');
  const items = response.data?.items ?? [];
  const match = items.find(item => item.user === userId);
  return match?.team ?? null;
};

export const fetchDatasets = async () => {
  const response = await guardedGet<ApiResponse<Dataset[]>>('/accounts/datasets');
  return response.data ?? [];
};

export const fetchTablesByDataset = async (datasetId: string) => {
  const response = await guardedGet<ApiResponse<TableList>>(
    `/accounts/tables?dataset=${datasetId}`
  );
  return response.data;
};

export const fetchTableRecords = async (
  tableId: string,
  page = 1,
  detailed = false,
  demoMode = false
) => {
  const detailedFlag = detailed ? 1 : 0;
  const demoFlag = demoMode ? 1 : 0;
  const baseUrl = `/accounts/tables/records/${detailedFlag}/${demoFlag}/${tableId}?page=${page}`;
  return guardedGet<ApiResponse<TableRecords>>(baseUrl).then(response => response.data);
};

export const fetchTableRecordsByQuery = async (
  tableId: string,
  query: string,
  page = 1,
  detailed = false,
  demoMode = false
) => {
  const detailedFlag = detailed ? 1 : 0;
  const demoFlag = demoMode ? 1 : 0;
  const response = await guardedGet<ApiResponse<TableRecords>>(
    `/accounts/tables/records/${detailedFlag}/${demoFlag}/${tableId}?page=${page}&${query}`
  );
  return response.data;
};

export const fetchTableFields = async (tableId: string) => {
  const response = await guardedGet<ApiResponse<TableField[]>>(
    `/accounts/fields?table=${tableId}&page=all`
  );
  return response.data ?? [];
};

export const fetchTableFilters = async (tableId: string) => {
  const response = await guardedGet<ApiResponse<TableFiltersResponse>>(
    `/accounts/tables/filters/${tableId}`
  );
  return response.data;
};

export const fetchAutocompleteOptions = async (payload: {
  tableId: string;
  fieldId?: string;
  toShowId?: string;
  query: string;
  limit?: number;
}) => {
  const response = await guardedGet<ApiResponse<Record<string, unknown>[]>>(
    `/accounts/tables/autocomplete/${payload.tableId}?field=${payload.fieldId ?? ''}&toShow=${payload.toShowId ?? ''}&s=${payload.query ?? ''}&limit=${payload.limit ?? 120}`
  );
  return response.data ?? [];
};

export const fetchHistoricAnalytics = async (userId: string) => {
  const response = await guardedGet<ApiResponse<Record<string, unknown>[]>>(
    `/accounts/analytics/historic/${userId}`
  );
  return response.data ?? [];
};

export const requestPasswordRecovery = async (email: string) => {
  return apiClient.post<ApiResponse<Record<string, unknown>>>('/accounts/recovery', { email });
};

export const resetPassword = async (payload: {
  token: string;
  password: string;
  repassword: string;
}) => {
  return apiClient.post<ApiResponse<Record<string, unknown>>>('/accounts/reset', payload);
};

export const switchAccount = async (payload: {
  account: string;
  dataset: string;
  table?: string;
}) => {
  return guardedPost<ApiResponse<Record<string, unknown>>>('/accounts/switch', payload);
};

export const postAnalyticsEvent = async (payload: Record<string, unknown>) => {
  return apiClient.post<ApiResponse<Record<string, unknown>>>('/analytics', payload);
};

export const createReport = async (payload: Record<string, unknown>) => {
  const response = await guardedPost<ApiResponse<SavedReportItem>>('/accounts/reports', payload);
  return response.data;
};

export const fetchReportStatus = async () => {
  const response = await guardedPost<ApiResponse<{ status?: string }>>('/accounts/reports/status');
  return response.data;
};

export const fetchSavedReportsByDataset = async (datasetId: string) => {
  const response = await guardedGet<ApiResponse<SavedReportListResponse>>(
    `/accounts/reports/dataset/${datasetId}`
  );
  const data = response.data as SavedReportListResponse | SavedReportItem[] | undefined;

  if (Array.isArray(data)) {
    return { items: data };
  }

  if (data && Array.isArray(data.items)) {
    return data;
  }

  return { items: [] };
};

export const deleteSavedReport = async (reportId: string) => {
  const response = await guardedDelete<ApiResponse<Record<string, unknown>>>(
    `/accounts/reports/${reportId}`
  );
  return response.data;
};

export const updateUserSettings = async (payload: UpdateSettingsPayload) => {
  return guardedPut<ApiResponse<Record<string, unknown>>>('/accounts/update/settings', payload);
};

export const createPaymentOrder = async (amount: number) => {
  const payload: CreatePaymentOrderPayload = { amount };
  const response = await apiClient.post<ApiResponse<PaymentOrder>>('/accounts/payments', payload);
  return response.data;
};

export const confirmPaymentFromPayu = async (payload: PayuConfirmationPayload) => {
  const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
    '/accounts/payments/confirmation',
    payload
  );
  return response.data;
};

export const fetchPaymentHistory = async (page = 1) => {
  const path = page > 1 ? `/accounts/payments?page=${page}` : '/accounts/payments';
  const response = await guardedGet<ApiResponse<PaymentHistoryResponse>>(path);
  return response.data;
};

export const checkEmailRequest = async (email: string) => {
  const response = await apiClient.post<ApiResponse<{ available?: boolean }>>(
    '/accounts/signup/checkEmail',
    { email }
  );
  return response.data;
};

export const registerDemoRequest = async (payload: SignupDemoPayload) => {
  const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
    '/accounts/signup/hold',
    payload
  );
  return response.data;
};

export const checkCompanyRequest = async (nit: string) => {
  const response = await apiClient.post<ApiResponse<CheckCompanyResponse>>(
    '/accounts/signup/checkCompany',
    { nit }
  );
  return response.data;
};

export const registerUserRequest = async (payload: RegisterPayload) => {
  const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
    '/accounts/signup',
    payload
  );
  return response.data;
};

export const fetchCountries = async () => {
  const response = await apiClient.get<ApiResponse<CountryItem[]>>('/countries/?page=all');
  return response.data ?? [];
};

export const verifyInvitationRequest = async (token: string) => {
  const response = await apiClient.get<ApiResponse<InvitationUserData>>(
    `/accounts/team/invitation?token=${token}`
  );
  return response.data;
};

export const acceptInvitationRequest = async (payload: AcceptInvitationPayload) => {
  const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
    '/accounts/team/accept',
    payload
  );
  return response.data;
};
